/**
 * Employee 360 (Phase 8).
 *
 * The effectiveness block degrades honestly: discovery found no crosswalk
 * between the activity id namespace and the commission id namespace, so where
 * identity resolution is not confident the ratios are withheld and the reason
 * is stated, rather than a plausible number being shown.
 */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import type { EChartsOption } from "echarts";

import { Chart } from "@/components/charts/Chart";
import { Disclosure } from "@/components/cards/Disclosure";
import { DataTable, type Column } from "@/components/tables/DataTable";
import { KpiCard } from "@/components/cards/KpiCard";
import { JourneyTimeline } from "@/components/timeline/JourneyTimeline";
import { useEmployeeProfile, useTimeline } from "@/hooks/useAnalytics";
import { count, inr, pct, shortDate } from "@/utils/format";

interface PortfolioRow {
  client_id: string; label: string; credits: number; commission: number;
  revenue: number; last_credit: string | null; share_pct: number; cumulative_pct: number;
}

export function EmployeeProfile() {
  const { personKey } = useParams<{ personKey: string }>();
  const [tab, setTab] = useState<"Overview" | "Timeline">("Overview");
  const { data, isLoading } = useEmployeeProfile(personKey);
  // The period filter narrows the journey; this reveals the whole
  // relationship, which is the point of the view when it is what you want.
  const [allTime, setAllTime] = useState(false);
  const journey = useTimeline("employee", personKey, allTime);

  const summary = data?.summary as
    | {
        identity: Record<string, string | number | boolean | null>;
        productivity: Record<string, number | null>;
        business: Record<string, number | null>;
        effectiveness: Record<string, number | string | boolean | null>;
        discipline: Record<string, number | null>;
      }
    | undefined;

  if (isLoading) return <div className="skeleton h-64" />;
  if (!summary) return <div className="card p-6">Employee not found.</div>;

  const { identity, productivity, business, effectiveness, discipline } = summary;
  const portfolio = (data?.breakdown ?? []) as unknown as PortfolioRow[];
  const top5 = portfolio.slice(0, 5).reduce((sum, r) => sum + r.share_pct, 0);

  const trend = (data?.trend ?? []) as Array<Record<string, number | string>>;
  const trendOption: EChartsOption = {
    tooltip: { trigger: "axis" },
    legend: { data: ["Activities", "Clients touched", "Commission"] },
    xAxis: { type: "category", data: trend.map((t) => String(t.bucket)) },
    yAxis: [{ type: "value" }, { type: "value", axisLabel: { formatter: (v: number) => inr(v) } }],
    series: [
      { name: "Activities", type: "bar", barMaxWidth: 14, data: trend.map((t) => Number(t.activities ?? 0)) },
      { name: "Clients touched", type: "line", smooth: true, showSymbol: false, data: trend.map((t) => Number(t.clients ?? 0)) },
      {
        name: "Commission", type: "line", yAxisIndex: 1, smooth: true, showSymbol: false,
        data: trend.map((t) => Number(t.commission ?? 0)),
      },
    ],
  };

  const columns: Column<PortfolioRow>[] = [
    { key: "label", header: "Client" },
    { key: "credits", header: "Referrals", align: "right" },
    { key: "revenue", header: "Revenue", align: "right", render: (r) => inr(r.revenue), value: (r) => r.revenue },
    { key: "commission", header: "Commission", align: "right", render: (r) => inr(r.commission), value: (r) => r.commission },
    { key: "share_pct", header: "Share", align: "right", render: (r) => pct(r.share_pct), value: (r) => r.share_pct },
    { key: "cumulative_pct", header: "Cumulative", align: "right", render: (r) => pct(r.cumulative_pct), value: (r) => r.cumulative_pct },
    { key: "last_credit", header: "Last referral", align: "right", render: (r) => shortDate(r.last_credit) },
  ];

  return (
    <div className="space-y-5">
      <header className="card p-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label">{String(identity.designation ?? "Marketing")} · {String(identity.branch ?? "")}</div>
          <h1 className="font-display font-bold text-[26px] tracking-tight mt-0.5">
            {String(identity.name)}
          </h1>
          <p className="text-[13px] text-ink-muted mt-1 font-mono">
            {String(identity.source_id_count)} source id(s) · identity confidence{" "}
            {String(identity.identity_confidence)}
          </p>
        </div>
        {identity.needs_identity_review ? (
          <span className="chip border-warn/30 bg-warn-soft text-warn">
            <AlertTriangle size={10} /> identity needs review
          </span>
        ) : null}
      </header>

      <nav className="flex gap-1 border-b border-rule">
        {(["Overview", "Timeline"] as const).map((name) => (
          <button
            key={name}
            onClick={() => setTab(name)}
            className={`px-3 py-2 text-[13.5px] border-b-2 -mb-px ${
              tab === name
                ? "border-petrol text-petrol font-medium"
                : "border-transparent text-ink-muted hover:text-ink"
            }`}
          >
            {name}
            {name === "Timeline" && journey.data?.metadata.count ? (
              <span className="ml-1.5 text-[11px] text-ink-faint tnum">
                {journey.data.metadata.count}
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      {tab === "Timeline" ? (
        <JourneyTimeline
          allTime={allTime}
          onAllTimeChange={setAllTime}
          events={journey.data?.events ?? []}
          mode="employee"
          subject={String(identity.name)}
          loading={journey.isLoading}
        />
      ) : (
      <>
      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(198px, 1fr))" }}>
        <KpiCard label="Activities" value={count(productivity.activities)}
                 footnote={`${productivity.activities_per_working_day ?? "—"} per working day`} />
        <KpiCard label="Clients touched" value={count(productivity.unique_clients)} />
        <KpiCard label="Admissions" value={count(business.admissions)} />
        <KpiCard label="Revenue influenced" value={inr(business.revenue)} />
        <KpiCard label="Commission" value={inr(business.commission_approved)} />
        <KpiCard label="Follow-up compliance" value={pct(discipline.compliance_pct)}
                 coveragePct={discipline.known_pct}
                 footnote="of follow-ups whose outcome is knowable" />
      </div>

      <div className="card p-4">
        <h2 className="font-display font-semibold text-[17px]">Effectiveness</h2>
        {effectiveness.available ? (
          <div className="grid gap-3 mt-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
            {[
              ["Leads / activity", effectiveness.leads_per_activity],
              ["Conversions / activity", effectiveness.conversions_per_activity],
              ["Conversion / lead", effectiveness.conversion_per_lead_pct],
              ["Revenue / activity", effectiveness.revenue_per_activity],
              ["Revenue / client", effectiveness.revenue_per_client],
            ].map(([label, value]) => (
              <div key={String(label)} className="border-t border-rule pt-2">
                <div className="label">{String(label)}</div>
                <div className="font-display font-semibold text-lg tnum mt-0.5">
                  {String(label).startsWith("Revenue")
                    ? inr(Number(value))
                    : String(label).includes("/ lead")
                      ? pct(Number(value))
                      : Number(value ?? 0).toFixed(3)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-3 p-3 rounded-md bg-warn-soft border border-warn/25">
            <p className="text-[13.5px] text-ink-soft">
              <strong>Ratios withheld.</strong> This person&rsquo;s activity ids and
              commission ids could not be tied together with sufficient confidence,
              so effort and outcome are reported separately rather than divided.
            </p>
            <p className="text-[12.5px] text-ink-muted mt-1.5">
              The source has no crosswalk column between its five employee id
              conventions. Resolve the link in Administration → Identity review.
            </p>
          </div>
        )}
      </div>

      <div className="card p-4">
        <h2 className="font-display font-semibold text-[17px] mb-3">Weekly trend</h2>
        <Chart option={trendOption} height={280} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-semibold text-[17px]">Client portfolio</h2>
          {top5 > 60 && (
            <span className="chip border-warn/30 bg-warn-soft text-warn">
              concentration: top 5 = {top5.toFixed(0)}%
            </span>
          )}
        </div>
        <DataTable<PortfolioRow>
          rows={portfolio}
          columns={columns}
          rowKey={(r) => r.client_id}
          exportName={`employee-${personKey}-portfolio`}
          footnote="Cumulative share shows how concentrated this book is — the Pareto point is where cumulative crosses 80%."
        />
      </div>

      <Disclosure quality={data?.quality} metadata={data?.metadata} />
      </>
      )}
    </div>
  );
}
